import * as THREE from 'three';

import {
  DEFAULT_ACID_GAS_SETUP,
  FLASK_CAPACITY_ML,
  GAS_SYRINGE_CAPACITY_ML,
  type AcidGasSceneState,
  type AcidGasSetup,
  type SolidForm,
  acidGasSceneState,
  calculateAcidGas,
  gasReactionById,
} from './acidGasExplore';
import {
  type LabSceneFactory,
  createLabStage,
  labGlassEdgeMaterial,
  labGlassMaterial,
  refractiveLiquidMaterial,
  seeded,
} from './labScene3d';

/**
 * "How much gas?" apparatus: a conical flask of acid with the solid on its floor, a bung and
 * delivery tube, and a gas syringe whose plunger follows the model gas volume. Every value comes
 * from acidGasExplore.ts. A teaching view, not to scale.
 */

// World units. The flask stands on the bench; the syringe is held level by a clamp stand.
const BENCH_Y = -1.82;
const FLASK_X = -2.0;
const FLASK_R = 1.1;
const CONE_START = 0.22;
const NECK_Y = 1.8;
const NECK_R = 0.3;
const TOP_Y = 2.45;
const WALL = 0.05;
const SYRINGE_Y = 1.15;
const SYRINGE_R = 0.3;
const BARREL_START = -0.4;
const BARREL_LENGTH = 2.4;
const PISTON_GAP = 0.06;
const ROD_LENGTH = 2.5;
const MAX_PIECES = 12;
const FILINGS_PER_PIECE = 4;
const BUBBLES = 48;
const VIGOUR_BUBBLES = { none: 0, slow: 10, steady: 24, rapid: 48 } as const;

/** Inner radius of the flask at height y above its floor. */
function innerRadius(y: number): number {
  if (y <= CONE_START) return FLASK_R - WALL - 0.01;
  if (y >= NECK_Y) return NECK_R - WALL;
  const t = (y - CONE_START) / (NECK_Y - CONE_START);
  return FLASK_R - WALL - 0.01 + (NECK_R - FLASK_R) * t;
}

const FLOOR = WALL;
const SLICES = 240;
/** Cumulative inner volume (world units³) at each slice, so a millilitre level can be looked up. */
const VOLUME_TABLE = (() => {
  const table = [0];
  const dy = (NECK_Y - FLOOR) / SLICES;
  for (let index = 1; index <= SLICES; index += 1) {
    const r = innerRadius(FLOOR + (index - 0.5) * dy);
    table.push(table[index - 1] + Math.PI * r * r * dy);
  }
  return table;
})();

/** Liquid height above the inner floor for a volume, with the flask full to the neck at its capacity. */
export function flaskLevel(volumeMl: number): number {
  const target = (THREE.MathUtils.clamp(volumeMl, 0, FLASK_CAPACITY_ML) / FLASK_CAPACITY_ML) * VOLUME_TABLE[SLICES];
  const dy = (NECK_Y - FLOOR) / SLICES;
  for (let index = 1; index <= SLICES; index += 1) {
    if (VOLUME_TABLE[index] >= target) {
      const below = VOLUME_TABLE[index - 1];
      const share = (target - below) / Math.max(VOLUME_TABLE[index] - below, 1e-9);
      return (index - 1 + share) * dy;
    }
  }
  return NECK_Y - FLOOR;
}

function liquidGeometry(height: number): THREE.LatheGeometry {
  const points = [new THREE.Vector2(0, FLOOR)];
  const steps = 24;
  for (let index = 0; index <= steps; index += 1) {
    const y = FLOOR + (height * index) / steps;
    points.push(new THREE.Vector2(innerRadius(y), y));
  }
  points.push(new THREE.Vector2(0, FLOOR + height));
  return new THREE.LatheGeometry(points, 96);
}

export const createAcidGasScene: LabSceneFactory<AcidGasSceneState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion, {
    cameraPosition: [0.9, 2.7, 11.8],
    target: [0.65, 0.15, 0],
    benchY: BENCH_Y,
  });
  const { scene, keep } = stage;
  const white = palette.glass.clone();

  // Conical flask: lathe with wall thickness, a thicker rim and base ring.
  const outer = [
    [0, 0], [FLASK_R - 0.12, 0], [FLASK_R - 0.02, 0.04], [FLASK_R, CONE_START],
    [NECK_R, NECK_Y], [NECK_R, TOP_Y], [NECK_R + 0.06, TOP_Y + 0.03], [NECK_R + 0.04, TOP_Y + 0.08], [NECK_R - WALL, TOP_Y + 0.06],
  ];
  const inner = [...Array.from({ length: 13 }, (_, index) => {
    const y = TOP_Y - (index / 12) * (TOP_Y - FLOOR);
    return [innerRadius(y), y];
  }), [0, FLOOR]];
  const flaskPoints = [...outer, ...inner].map(([x, y]) => new THREE.Vector2(x, y));
  const flask = new THREE.Group();
  flask.position.set(FLASK_X, BENCH_Y, 0);
  const flaskBody = new THREE.Mesh(keep(new THREE.LatheGeometry(flaskPoints, 112)), labGlassMaterial(stage));
  flaskBody.renderOrder = 10;
  const edge = labGlassEdgeMaterial(stage);
  const baseRing = new THREE.Mesh(keep(new THREE.TorusGeometry(FLASK_R - 0.06, 0.045, 12, 112)), edge);
  baseRing.rotation.x = Math.PI / 2;
  baseRing.position.y = 0.05;
  const lip = new THREE.Mesh(keep(new THREE.TorusGeometry(NECK_R + 0.03, 0.04, 12, 64)), edge);
  lip.rotation.x = Math.PI / 2;
  lip.position.y = TOP_Y + 0.05;
  baseRing.renderOrder = 11;
  lip.renderOrder = 11;
  flask.add(flaskBody, baseRing, lip);
  scene.add(flask);

  // Acid: refractive liquid whose lathe shape follows the volume.
  const colourless = palette.ice.clone();
  const paleGreen = palette.liquid.clone().lerp(palette.glow, 0.55).lerp(white, 0.3);
  const acidMaterial = refractiveLiquidMaterial(stage, colourless, { tint: 0.45, attenuationDistance: 3.5 });
  let liquidHeight = -1;
  const acid = new THREE.Mesh(liquidGeometry(0.1), acidMaterial);
  acid.position.copy(flask.position);
  scene.add(acid);
  const tint = new THREE.Color();

  // Rubber bung and glass delivery tube to the syringe nozzle.
  const rubber = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(palette.heat, 0.3), roughness: 0.75 }));
  const bung = new THREE.Mesh(keep(new THREE.CylinderGeometry(NECK_R + 0.05, NECK_R - 0.06, 0.42, 40)), rubber);
  bung.position.set(FLASK_X, BENCH_Y + TOP_Y + 0.02, 0);
  bung.castShadow = true;
  scene.add(bung);
  const nozzleX = BARREL_START - 0.28;
  const path = new THREE.CatmullRomCurve3([
    new THREE.Vector3(FLASK_X, BENCH_Y + TOP_Y - 0.3, 0),
    new THREE.Vector3(FLASK_X, SYRINGE_Y - 0.25, 0),
    new THREE.Vector3(FLASK_X + 0.3, SYRINGE_Y, 0),
    new THREE.Vector3(nozzleX, SYRINGE_Y, 0),
  ], false, 'catmullrom', 0.1);
  const tubeMaterial = labGlassEdgeMaterial(stage);
  const deliveryTube = new THREE.Mesh(keep(new THREE.TubeGeometry(path, 64, 0.05, 12, false)), tubeMaterial);
  deliveryTube.renderOrder = 12;
  scene.add(deliveryTube);

  // Gas syringe: glass barrel along x with graduation marks, nozzle, piston, rod and handle.
  const syringe = new THREE.Group();
  syringe.position.set(0, SYRINGE_Y, 0);
  const barrelMaterial = labGlassMaterial(stage, 0.22);
  barrelMaterial.side = THREE.DoubleSide;
  const barrel = new THREE.Mesh(keep(new THREE.CylinderGeometry(SYRINGE_R, SYRINGE_R, BARREL_LENGTH, 48, 1, true)), barrelMaterial);
  barrel.rotation.z = Math.PI / 2;
  barrel.position.x = BARREL_START + BARREL_LENGTH / 2;
  barrel.renderOrder = 12;
  const endCap = new THREE.Mesh(keep(new THREE.CircleGeometry(SYRINGE_R, 48)), barrelMaterial);
  endCap.rotation.y = -Math.PI / 2;
  endCap.position.x = BARREL_START;
  const nozzle = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.07, 0.09, 0.3, 20)), edge);
  nozzle.rotation.z = Math.PI / 2;
  nozzle.position.x = BARREL_START - 0.14;
  const rimGeometry = keep(new THREE.TorusGeometry(SYRINGE_R + 0.01, 0.035, 10, 48));
  const startRim = new THREE.Mesh(rimGeometry, edge);
  startRim.rotation.y = Math.PI / 2;
  startRim.position.x = BARREL_START;
  const endRim = new THREE.Mesh(rimGeometry, edge);
  endRim.rotation.y = Math.PI / 2;
  endRim.position.x = BARREL_START + BARREL_LENGTH;
  syringe.add(barrel, endCap, nozzle, startRim, endRim);
  const markMaterial = keep(new THREE.MeshBasicMaterial({ color: palette.metal, transparent: true, opacity: 0.55 }));
  const markGeometry = keep(new THREE.BoxGeometry(0.012, 1, 0.012));
  const usable = BARREL_LENGTH - PISTON_GAP * 2;
  for (let mark = 0; mark <= 10; mark += 1) {
    const tick = new THREE.Mesh(markGeometry, markMaterial);
    tick.scale.y = mark % 5 === 0 ? 0.2 : 0.11;
    tick.position.set(BARREL_START + PISTON_GAP + (mark / 10) * usable, SYRINGE_R * 0.72, SYRINGE_R * 0.72);
    tick.rotation.x = -Math.PI / 4;
    syringe.add(tick);
  }
  // Collected gas: a faint column between the nozzle end and the piston, a second cue beside the number.
  const gasMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.steam, transparent: true, opacity: 0.16, roughness: 0.4, depthWrite: false }));
  const gas = new THREE.Mesh(keep(new THREE.CylinderGeometry(SYRINGE_R - 0.03, SYRINGE_R - 0.03, 1, 32)), gasMaterial);
  gas.rotation.z = Math.PI / 2;
  gas.renderOrder = 13;
  const plunger = new THREE.Group();
  const pistonMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(white, 0.2), roughness: 0.55 }));
  const piston = new THREE.Mesh(keep(new THREE.CylinderGeometry(SYRINGE_R - 0.02, SYRINGE_R - 0.02, 0.12, 40)), pistonMaterial);
  piston.rotation.z = Math.PI / 2;
  const rodMaterial = keep(new THREE.MeshPhysicalMaterial({ color: white.clone().lerp(palette.metal, 0.12), roughness: 0.3, clearcoat: 0.6 }));
  const rod = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.045, 0.045, ROD_LENGTH, 16)), rodMaterial);
  rod.rotation.z = Math.PI / 2;
  rod.position.x = ROD_LENGTH / 2;
  const handle = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.24, 0.24, 0.06, 32)), rodMaterial);
  handle.rotation.z = Math.PI / 2;
  handle.position.x = ROD_LENGTH;
  plunger.add(piston, rod, handle);
  syringe.add(gas, plunger);
  scene.add(syringe);

  // Clamp stand behind the syringe.
  const steel = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(white, 0.45), metalness: 0.8, roughness: 0.3 }));
  const standX = BARREL_START + BARREL_LENGTH * 0.55;
  const basePlate = new THREE.Mesh(keep(new THREE.BoxGeometry(1.4, 0.1, 0.9)), steel);
  basePlate.position.set(standX, BENCH_Y + 0.05, -0.9);
  const post = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.05, 0.05, SYRINGE_Y - BENCH_Y + 0.6, 16)), steel);
  post.position.set(standX, (SYRINGE_Y + BENCH_Y + 0.6) / 2, -0.9);
  const arm = new THREE.Mesh(keep(new THREE.BoxGeometry(0.1, 0.1, 0.6)), steel);
  arm.position.set(standX, SYRINGE_Y, -0.55);
  const clamp = new THREE.Mesh(keep(new THREE.TorusGeometry(SYRINGE_R + 0.04, 0.04, 8, 32)), steel);
  clamp.rotation.y = Math.PI / 2;
  clamp.position.set(standX, SYRINGE_Y, 0);
  [basePlate, post, arm, clamp].forEach((part) => {
    part.castShadow = true;
    scene.add(part);
  });

  // Solid on the flask floor: one instanced shape per form. Opaque, so the refractive acid shows it.
  const metalMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(white, 0.55), metalness: 0.85, roughness: 0.35 }));
  const whiteSolidMaterial = keep(new THREE.MeshPhysicalMaterial({ color: white.clone().lerp(palette.metal, 0.05), roughness: 0.85 }));
  const shapes: Record<SolidForm, THREE.BufferGeometry> = {
    ribbon: keep(new THREE.BoxGeometry(0.42, 0.025, 0.1)),
    granules: keep(new THREE.IcosahedronGeometry(0.1, 0)),
    filings: keep(new THREE.BoxGeometry(0.14, 0.02, 0.025)),
    chips: keep(new THREE.DodecahedronGeometry(0.13, 0)),
    powder: keep(new THREE.SphereGeometry(0.5, 32, 12, 0, Math.PI * 2, 0, Math.PI / 2)),
  };
  const solids = (Object.keys(shapes) as SolidForm[]).reduce((all, form) => {
    const count = form === 'filings' ? MAX_PIECES * FILINGS_PER_PIECE : form === 'powder' ? 1 : MAX_PIECES;
    const mesh = new THREE.InstancedMesh(shapes[form], metalMaterial, count);
    mesh.castShadow = true;
    mesh.visible = false;
    scene.add(mesh);
    all[form] = mesh;
    return all;
  }, {} as Record<SolidForm, THREE.InstancedMesh>);
  const spots = Array.from({ length: MAX_PIECES * FILINGS_PER_PIECE }, (_, index) => {
    const angle = seeded(index, 21) * Math.PI * 2;
    const radius = Math.sqrt(seeded(index, 22)) * (FLASK_R - 0.35);
    return { x: Math.cos(angle) * radius, z: Math.sin(angle) * radius, yaw: seeded(index, 23) * Math.PI, tilt: (seeded(index, 24) - 0.5) * 0.5 };
  });

  // Bubbles rise from the solid while the reaction is going. Transparent, not refractive.
  const bubbles = new THREE.InstancedMesh(
    keep(new THREE.SphereGeometry(0.035, 12, 8)),
    keep(new THREE.MeshPhysicalMaterial({ color: white, roughness: 0.05, transparent: true, opacity: 0.75, clearcoat: 1, envMapIntensity: 2.5 })),
    BUBBLES,
  );
  scene.add(bubbles);

  stage.finish();

  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const scale = new THREE.Vector3();
  const hidden = new THREE.Matrix4().makeScale(0, 0, 0);
  const floorY = BENCH_Y + FLOOR;

  return {
    update: (state, t, playing) => {
      // Acid level and colour.
      const height = Math.max(flaskLevel(state.acidVolumeMl), 0.02);
      if (Math.abs(height - liquidHeight) > 1e-4) {
        const next = liquidGeometry(height);
        acid.geometry.dispose();
        acid.geometry = next;
        liquidHeight = height;
      }
      const share = state.solutionTint === 'pale-green' ? THREE.MathUtils.clamp(state.tintShare, 0, 1) : 0;
      tint.copy(colourless).lerp(paleGreen, share);
      acidMaterial.attenuationColor.copy(tint);
      acidMaterial.color.copy(tint).lerp(white, 0.45);

      // Solid pieces: the count follows the mass; each piece shrinks with the share left.
      const size = Math.cbrt(THREE.MathUtils.clamp(state.solidLeftShare, 0, 1));
      (Object.keys(solids) as SolidForm[]).forEach((form) => {
        const mesh = solids[form];
        mesh.visible = form === state.solidForm && size > 0.04;
        if (!mesh.visible) return;
        mesh.material = state.solidTone === 'metal' ? metalMaterial : whiteSolidMaterial;
        if (form === 'powder') {
          const heap = 0.45 + 0.55 * Math.cbrt(state.pieces / MAX_PIECES);
          position.set(FLASK_X, floorY, 0);
          scale.set(heap * size, heap * size * 0.4, heap * size);
          mesh.setMatrixAt(0, matrix.compose(position, quaternion.identity(), scale));
          mesh.instanceMatrix.needsUpdate = true;
          return;
        }
        const perPiece = form === 'filings' ? FILINGS_PER_PIECE : 1;
        for (let index = 0; index < mesh.count; index += 1) {
          if (index >= state.pieces * perPiece) {
            mesh.setMatrixAt(index, hidden);
            continue;
          }
          const spot = spots[index];
          const lift = form === 'chips' ? 0.1 : form === 'granules' ? 0.08 : 0.02;
          position.set(FLASK_X + spot.x, floorY + lift * size, spot.z);
          euler.set(form === 'ribbon' ? 0 : spot.tilt, spot.yaw, form === 'ribbon' ? spot.tilt * 0.3 : spot.tilt);
          mesh.setMatrixAt(index, matrix.compose(position, quaternion.setFromEuler(euler), scale.setScalar(size)));
        }
        mesh.instanceMatrix.needsUpdate = true;
      });

      // Bubbles from the solid to the surface while reacting.
      const active = state.reacting && size > 0.04 ? VIGOUR_BUBBLES[state.vigour] : 0;
      for (let index = 0; index < BUBBLES; index += 1) {
        if (index >= active) {
          bubbles.setMatrixAt(index, hidden);
          continue;
        }
        const spot = spots[index % Math.max(state.pieces, 1)];
        const progress = (t * (0.45 + seeded(index, 31) * 0.5) + seeded(index, 32)) % 1;
        const rise = Math.max(height - 0.1, 0.05) * progress;
        const radius = innerRadius(FLOOR + rise) - 0.08;
        const x = THREE.MathUtils.clamp(spot.x + (seeded(index, 33) - 0.5) * 0.2, -radius, radius);
        const z = THREE.MathUtils.clamp(spot.z + Math.sin(t * 3 + index) * 0.03, -radius, radius);
        position.set(FLASK_X + x, floorY + 0.06 + rise, z);
        bubbles.setMatrixAt(index, matrix.compose(position, quaternion.identity(), scale.setScalar(0.6 + progress * 1.2)));
      }
      bubbles.instanceMatrix.needsUpdate = true;

      // Plunger and gas column follow the model gas volume.
      const fraction = THREE.MathUtils.clamp(state.syringeMl / GAS_SYRINGE_CAPACITY_ML, 0, 1);
      const pistonX = BARREL_START + PISTON_GAP + fraction * usable;
      plunger.position.x = pistonX;
      gas.visible = fraction > 0.002;
      gas.scale.y = Math.max(pistonX - BARREL_START, 0.001);
      gas.position.x = (BARREL_START + pistonX) / 2;

      stage.render(playing);
    },
    resize: stage.resize,
    dispose: () => {
      acid.geometry.dispose();
      stage.dispose();
    },
  };
};

function previewState(reactionId: string, setup: AcidGasSetup): AcidGasSceneState {
  return acidGasSceneState(calculateAcidGas(gasReactionById(reactionId), setup), setup);
}

/** Named states for screenshot review of every visible change. */
export const ACID_GAS_PREVIEW_STATES: Record<string, AcidGasSceneState> = {
  'magnesium-start': previewState('HCl-Mg', { ...DEFAULT_ACID_GAS_SETUP, massG: 0.1, progressPct: 0 }),
  'magnesium-halfway': previewState('HCl-Mg', { ...DEFAULT_ACID_GAS_SETUP, massG: 0.1, progressPct: 50 }),
  'magnesium-finished': previewState('HCl-Mg', DEFAULT_ACID_GAS_SETUP),
  'marble-chips-acid-limits': previewState('HCl-CaCO3', { massG: 1, concentrationMolL: 0.5, volumeMl: 10, progressPct: 100 }),
  'iron-pale-green': previewState('HCl-Fe', { massG: 0.5, concentrationMolL: 1, volumeMl: 80, progressPct: 60 }),
  'syringe-full': previewState('H2SO4-Mg', { massG: 0.5, concentrationMolL: 1, volumeMl: 50, progressPct: 100 }),
};
